import React, { useState } from 'react';
import { 
  Circle, 
  Square, 
  Diamond, 
  ArrowRight, 
  Minus, 
  ToggleLeft, 
  Sliders,
  User,
  Layers,
  CheckCircle, 
  XCircle, 
  BookOpen,
  Download,
  Eye,
  Target,
  Zap,
  Compass,
  Star,
} from 'lucide-react';

export const VisualGrammarSystem: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'overview' | 'primitives' | 'syntax' | 'altitude' | 'markers' | 'examples' | 'usage' | 'reference'>('overview');

  const sections = {
    overview: {
      title: 'Visual Grammar Overview',
      icon: <BookOpen className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border border-consulting-slate-blue-200 p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-3">Purpose</h3>
            <p className="text-charcoal-700 leading-relaxed">
              Why we use a visual language: to clarify distinctions, relationships, and flow in prioritization and decision-making. 
              Our visual grammar ensures every diagram communicates clearly and consistently.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-sage-50 to-amber-50 border border-sage-200 p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-3">Analogy</h3>
            <p className="text-charcoal-700 leading-relaxed">
              Just as language has words and grammar, our diagrams use "primitives" (building blocks) and "rules of combination" (syntax). 
              This creates a systematic approach to visual communication.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-4 bg-[var(--bg-raised)] border border-[var(--rule)]">
              <h4 className="font-semibold text-charcoal-900 mb-2">Core Principles</h4>
              <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                <li>Every visual element has meaning</li>
                <li>Consistent symbols create clarity</li>
                <li>Relationships are explicit, not implied</li>
                <li>Complexity scales with detail level</li>
              </ul>
            </div>
            
            <div className="p-4 bg-[var(--bg-raised)] border border-[var(--rule)]">
              <h4 className="font-semibold text-charcoal-900 mb-2">Key Benefits</h4>
              <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                <li>Faster diagram creation</li>
                <li>Consistent interpretation</li>
                <li>Reduced cognitive load</li>
                <li>Scalable complexity</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    primitives: {
      title: 'Core Visual Primitives',
      icon: <Circle className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Shapes & Symbols</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 bg-[var(--surface)]">
                <div className="flex items-center gap-3 mb-2">
                  <Circle className="h-6 w-6 text-consulting-slate-blue-600" />
                  <span className="font-semibold text-charcoal-900">Circle</span>
                </div>
                <p className="text-sm text-charcoal-700">State or context</p>
              </div>
              
              <div className="p-4 bg-[var(--surface)]">
                <div className="flex items-center gap-3 mb-2">
                  <Square className="h-6 w-6 text-sage-600" />
                  <span className="font-semibold text-charcoal-900">Rectangle</span>
                </div>
                <p className="text-sm text-charcoal-700">Process or activity</p>
              </div>
              
              <div className="p-4 bg-[var(--surface)]">
                <div className="flex items-center gap-3 mb-2">
                  <Diamond className="h-6 w-6 text-[var(--mustard)]" />
                  <span className="font-semibold text-charcoal-900">Diamond</span>
                </div>
                <p className="text-sm text-charcoal-700">Decision point</p>
              </div>
              
              <div className="p-4 bg-[var(--surface)]">
                <div className="flex items-center gap-3 mb-2">
                  <ArrowRight className="h-6 w-6 text-consulting-slate-blue-600" />
                  <span className="font-semibold text-charcoal-900">Arrow</span>
                </div>
                <p className="text-sm text-charcoal-700">Causality or flow</p>
              </div>
              
              <div className="p-4 bg-[var(--surface)]">
                <div className="flex items-center gap-3 mb-2">
                  <Minus className="h-6 w-6 text-charcoal-600" />
                  <span className="font-semibold text-charcoal-900">Dashed Line</span>
                </div>
                <p className="text-sm text-charcoal-700">Influence, not causation</p>
              </div>
              
              <div className="p-4 bg-[var(--surface)]">
                <div className="flex items-center gap-3 mb-2">
                  <Square className="h-6 w-6 text-sage-600 border-2 border-dashed" />
                  <span className="font-semibold text-charcoal-900">Boundary</span>
                </div>
                <p className="text-sm text-charcoal-700">Threshold, scope, or frame</p>
              </div>
              
              <div className="p-4 bg-[var(--surface)]">
                <div className="flex items-center gap-3 mb-2">
                  <ToggleLeft className="h-6 w-6 text-[var(--mustard)]" />
                  <span className="font-semibold text-charcoal-900">Toggle</span>
                </div>
                <p className="text-sm text-charcoal-700">Binary state</p>
              </div>
              
              <div className="p-4 bg-[var(--surface)]">
                <div className="flex items-center gap-3 mb-2">
                  <Sliders className="h-6 w-6 text-consulting-slate-blue-600" />
                  <span className="font-semibold text-charcoal-900">Dial/Slider</span>
                </div>
                <p className="text-sm text-charcoal-700">Range/continuum</p>
              </div>
              
              <div className="p-4 bg-[var(--surface)]">
                <div className="flex items-center gap-3 mb-2">
                  <User className="h-6 w-6 text-sage-600" />
                  <span className="font-semibold text-charcoal-900">Icon</span>
                </div>
                <p className="text-sm text-charcoal-700">Actor, force, or concept</p>
              </div>
            </div>
          </div>

          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Color Coding System</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-3">Semantic Colors</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-consulting-slate-blue-600 rounded"></div>
                    <span className="text-sm text-charcoal-700">Primary Blue - Steady, foundational</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-sage-600 rounded"></div>
                    <span className="text-sm text-charcoal-700">Sage Green - Growth, progress</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-[var(--mustard)] rounded"></div>
                    <span className="text-sm text-charcoal-700">Amber - Energy, attention</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-luminous-coral-600 rounded"></div>
                    <span className="text-sm text-charcoal-700">Coral - Urgent, critical</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-3">Usage Rules</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                  <li>Colors mapped to forces, states, urgency, complexity</li>
                  <li>Key color legend always present on diagrams</li>
                  <li>Consistent color meaning across all materials</li>
                  <li>Use color to reinforce, not replace, shape meaning</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    syntax: {
      title: 'Combination Rules & Syntax',
      icon: <Target className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">How Shapes Connect</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-3">Connection Rules</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-2">
                  <li>Arrows only originate from "causal" elements (rectangles) and point to effects (circles, diamonds)</li>
                  <li>Dials/sliders always "control" or influence a downstream process</li>
                  <li>Boundaries always wrap nested or scoped elements (no overlap unless denoting shared context)</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-3">Allowed Relationships</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-2">
                  <li>One decision can lead to multiple states (branching)</li>
                  <li>Feedback loops shown by closed arrows; recursion by looped paths</li>
                  <li>State shifts must cross a threshold symbol (doorway, double-line, or special icon)</li>
                  <li>Color or symbol must change at state shifts/thresholds</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Combination Anti-patterns</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 bg-[var(--coral-dim)] border border-[var(--coral)]">
                <h4 className="font-semibold text-[var(--coral)] mb-3 flex items-center gap-2">
                  <XCircle className="h-5 w-5" />
                  Never Do
                </h4>
                <ul className="list-disc list-inside text-[var(--coral)] text-sm space-y-1">
                  <li>Never connect two dials directly</li>
                  <li>Don't mix dashed and solid arrows unless showing distinction between influence and causation</li>
                  <li>Never omit legend/key on multi-layer diagrams</li>
                  <li>Don't use shapes inconsistently</li>
                </ul>
              </div>
              
              <div className="p-4 bg-[var(--green-dim)] border border-[var(--green)]">
                <h4 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-3 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Always Do
                </h4>
                <ul className="list-disc list-inside text-[var(--green)] dark:text-[var(--text-ter)] text-sm space-y-1">
                  <li>Include legend on every diagram</li>
                  <li>Use consistent shape meanings</li>
                  <li>Show clear causal relationships</li>
                  <li>Indicate state changes explicitly</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    altitude: {
      title: 'Diagram Altitude & Layering',
      icon: <Layers className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Levels of Detail</h3>
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 border border-consulting-slate-blue-200">
                <h4 className="font-semibold text-charcoal-900 mb-2">Level 1: Macro/Overview</h4>
                <p className="text-charcoal-700 text-sm mb-2">Minimal primitives, big picture, only core flows</p>
                <ul className="list-disc list-inside text-charcoal-600 text-xs space-y-1">
                  <li>High-level process flow</li>
                  <li>Major decision points only</li>
                  <li>Core causal relationships</li>
                  <li>Executive summary level</li>
                </ul>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-sage-50 to-amber-50 border border-sage-200">
                <h4 className="font-semibold text-charcoal-900 mb-2">Level 2: Major Sub-processes</h4>
                <p className="text-charcoal-700 text-sm mb-2">Major sub-processes or distinctions, labeled connections, some nested structures</p>
                <ul className="list-disc list-inside text-charcoal-600 text-xs space-y-1">
                  <li>Detailed process steps</li>
                  <li>Sub-decision points</li>
                  <li>Team-level interactions</li>
                  <li>Managerial overview level</li>
                </ul>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-amber-50 to-consulting-slate-blue-50 border border-[var(--mustard)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Level 3+: Full System Detail</h4>
                <p className="text-charcoal-700 text-sm mb-2">Every actor, process, state, force, and their relationships; all legend items used</p>
                <ul className="list-disc list-inside text-charcoal-600 text-xs space-y-1">
                  <li>Individual contributor level</li>
                  <li>All actors and forces</li>
                  <li>Complete state transitions</li>
                  <li>Implementation detail level</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    markers: {
      title: 'Specialized Visual Markers',
      icon: <Zap className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Thresholds/Triggers</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-[var(--surface)] text-center">
                <div className="w-8 h-8 border-2 border-charcoal-600 mx-auto mb-2"></div>
                <p className="text-sm text-charcoal-700">Gate/Doorway</p>
              </div>
              <div className="p-4 bg-[var(--surface)] text-center">
                <div className="w-8 h-1 bg-charcoal-600 mx-auto mb-2"></div>
                <div className="w-8 h-1 bg-charcoal-600 mx-auto mb-2"></div>
                <p className="text-sm text-charcoal-700">Double Line</p>
              </div>
              <div className="p-4 bg-[var(--surface)] text-center">
                <div className="w-8 h-8 bg-[var(--mustard)] rounded-full mx-auto mb-2 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
                <p className="text-sm text-charcoal-700">Special Icon</p>
              </div>
            </div>
            <p className="text-charcoal-700 text-sm mt-4">
              Used for state transitions, phase shifts, or crossing boundaries in process.
            </p>
          </div>

          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Context Markers</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="p-3 bg-consulting-slate-blue-100 text-center">
                <div className="w-6 h-6 bg-consulting-slate-blue-600 rounded-full mx-auto mb-1"></div>
                <p className="text-xs text-charcoal-700">Individual</p>
              </div>
              <div className="p-3 bg-sage-100 text-center">
                <div className="w-6 h-6 bg-sage-600 rounded-full mx-auto mb-1"></div>
                <p className="text-xs text-charcoal-700">Team</p>
              </div>
              <div className="p-3 bg-[var(--mustard-dim)] text-center">
                <div className="w-6 h-6 bg-[var(--mustard)] rounded-full mx-auto mb-1"></div>
                <p className="text-xs text-charcoal-700">Organization</p>
              </div>
              <div className="p-3 bg-luminous-coral-100 text-center">
                <div className="w-6 h-6 bg-luminous-coral-600 rounded-full mx-auto mb-1"></div>
                <p className="text-xs text-charcoal-700">Whole System</p>
              </div>
            </div>
            <p className="text-charcoal-700 text-sm mt-4">
              Tag or badge for individual/team/org/whole system context.
            </p>
          </div>

          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Forces</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-[var(--surface)]">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowRight className="h-4 w-4 text-luminous-coral-600" />
                  <span className="text-sm font-semibold text-charcoal-900">Urgency</span>
                </div>
                <p className="text-xs text-charcoal-700">Time pressure, deadlines</p>
              </div>
              <div className="p-4 bg-[var(--surface)]">
                <div className="flex items-center gap-2 mb-2">
                  <Layers className="h-4 w-4 text-[var(--mustard)]" />
                  <span className="text-sm font-semibold text-charcoal-900">Complexity</span>
                </div>
                <p className="text-xs text-charcoal-700">Interdependencies, layers</p>
              </div>
              <div className="p-4 bg-[var(--surface)]">
                <div className="flex items-center gap-2 mb-2">
                  <ToggleLeft className="h-4 w-4 text-sage-600" />
                  <span className="text-sm font-semibold text-charcoal-900">Reversibility</span>
                </div>
                <p className="text-xs text-charcoal-700">Can be undone or not</p>
              </div>
            </div>
            <p className="text-charcoal-700 text-sm mt-4">
              Arrowhead or visual motif layered atop relevant elements.
            </p>
          </div>
        </div>
      )
    },
    examples: {
      title: 'Visual Examples & Templates',
      icon: <Eye className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Valid Example Patterns</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-3">Core Patterns</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-2">
                  <li>Causal Chain</li>
                  <li>Prioritization Funnel</li>
                  <li>State Shift Sequence</li>
                  <li>Toggle vs. Dial vs. Multi-State Representation</li>
                  <li>Upstream/Downstream Mapping</li>
                  <li>System with Threshold/Trigger</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-3">Example Applications</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-2">
                  <li>DEGAP Framework visualization</li>
                  <li>7 C's diagnostic flow</li>
                  <li>Individual to organizational scaling</li>
                  <li>Decision tree with thresholds</li>
                  <li>Feedback loop mapping</li>
                  <li>Context switching patterns</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Invalid Examples</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 bg-[var(--coral-dim)] border border-[var(--coral)]">
                <h4 className="font-semibold text-[var(--coral)] mb-3">Common Mistakes</h4>
                <ul className="list-disc list-inside text-[var(--coral)] text-sm space-y-1">
                  <li>Misused arrows or color</li>
                  <li>Missing legend</li>
                  <li>Overlapping elements without explanation</li>
                  <li>Inconsistent shape meanings</li>
                  <li>Unclear causal relationships</li>
                </ul>
              </div>
              
              <div className="p-4 bg-[var(--green-dim)] border border-[var(--green)]">
                <h4 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-3">Quick Fixes</h4>
                <ul className="list-disc list-inside text-[var(--green)] dark:text-[var(--text-ter)] text-sm space-y-1">
                  <li>Always include legend</li>
                  <li>Use consistent symbols</li>
                  <li>Show clear connections</li>
                  <li>Indicate state changes</li>
                  <li>Test with others for clarity</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    usage: {
      title: 'Using the Visual Language',
      icon: <Compass className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">How to Build New Diagrams</h3>
            <div className="space-y-4">
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Step 1: Select Correct Primitives</h4>
                <p className="text-charcoal-700 text-sm">Choose shapes and symbols that match your concept (circle for state, rectangle for process, etc.)</p>
              </div>
              
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Step 2: Apply Combination Rules</h4>
                <p className="text-charcoal-700 text-sm">Connect elements following syntax rules (arrows from processes to states, etc.)</p>
              </div>
              
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Step 3: Include Legend and Altitude Marker</h4>
                <p className="text-charcoal-700 text-sm">Always include a legend and indicate the detail level (Level 1, 2, or 3+)</p>
              </div>
              
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Step 4: Review for Anti-patterns</h4>
                <p className="text-charcoal-700 text-sm">Check against the anti-pattern list to ensure clarity and consistency</p>
              </div>
            </div>
          </div>

          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">For Teams/Clients</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-3">Key Questions</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-2">
                  <li>"Does the visual make distinctions clearer?"</li>
                  <li>"Can someone else interpret this without explanation?"</li>
                  <li>"Are the relationships explicit?"</li>
                  <li>"Is the level of detail appropriate?"</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-3">Best Practices</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-2">
                  <li>Encourage consistent use and remixing for new scenarios</li>
                  <li>Start with simple patterns and add complexity</li>
                  <li>Test diagrams with different audiences</li>
                  <li>Iterate based on feedback</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    reference: {
      title: 'Reference Legend',
      icon: <Download className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Master Legend/Key</h3>
            <p className="text-charcoal-700 mb-4">
              All primitives, color codes, and their meaning on one page—printable and digital.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-3">Shapes & Symbols</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3">
                    <Circle className="h-4 w-4 text-consulting-slate-blue-600" />
                    <span className="text-charcoal-700">Circle - State or context</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Square className="h-4 w-4 text-sage-600" />
                    <span className="text-charcoal-700">Rectangle - Process or activity</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Diamond className="h-4 w-4 text-[var(--mustard)]" />
                    <span className="text-charcoal-700">Diamond - Decision point</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <ArrowRight className="h-4 w-4 text-consulting-slate-blue-600" />
                    <span className="text-charcoal-700">Arrow - Causality or flow</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Minus className="h-4 w-4 text-charcoal-600" />
                    <span className="text-charcoal-700">Dashed line - Influence</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-3">Color Meanings</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-consulting-slate-blue-600 rounded"></div>
                    <span className="text-charcoal-700">Primary Blue - Steady, foundational</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-sage-600 rounded"></div>
                    <span className="text-charcoal-700">Sage Green - Growth, progress</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-[var(--mustard)] rounded"></div>
                    <span className="text-charcoal-700">Amber - Energy, attention</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-luminous-coral-600 rounded"></div>
                    <span className="text-charcoal-700">Coral - Urgent, critical</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 border border-consulting-slate-blue-200 p-6">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Quick Reference Formats</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-[var(--bg-raised)] border border-[var(--rule)] text-center">
                <BookOpen className="h-8 w-8 text-consulting-slate-blue-600 mx-auto mb-2" />
                <p className="text-sm text-charcoal-700">Printable field card for workshops</p>
              </div>
              <div className="p-4 bg-[var(--bg-raised)] border border-[var(--rule)] text-center">
                <Download className="h-8 w-8 text-sage-600 mx-auto mb-2" />
                <p className="text-sm text-charcoal-700">Double-page spread in Brand Guidelines</p>
              </div>
              <div className="p-4 bg-[var(--bg-raised)] border border-[var(--rule)] text-center">
                <Eye className="h-8 w-8 text-[var(--mustard)] mx-auto mb-2" />
                <p className="text-sm text-charcoal-700">Slide in every presentation template</p>
              </div>
              <div className="p-4 bg-[var(--bg-raised)] border border-[var(--rule)] text-center">
                <User className="h-8 w-8 text-luminous-coral-600 mx-auto mb-2" />
                <p className="text-sm text-charcoal-700">PDF insert for client onboarding</p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  };

  const currentSection = sections[activeSection];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <BookOpen className="h-10 w-10 text-consulting-slate-blue-600" />
          <h1 className="text-4xl font-bold text-charcoal-900">Visual Grammar System</h1>
        </div>
        <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
          A systematic approach to visual communication that clarifies distinctions, relationships, and flow in prioritization and decision-making.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6 sticky top-8">
            <h2 className="text-xl font-bold text-charcoal-900 mb-4">Visual Grammar</h2>
            <div className="space-y-2">
              {Object.entries(sections).map(([key, section]) => (
                <button
                  key={key}
                  onClick={() => setActiveSection(key as any)}
                  className={`w-full text-left px-4 py-3  transition-colors ${
                    activeSection === key
                      ? 'bg-consulting-slate-blue-100 text-consulting-slate-blue-800 border-2 border-consulting-slate-blue-200'
                      : 'text-charcoal-700 hover:bg-[var(--surface)] border-2 border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {section.icon}
                    <div className="font-medium">{section.title}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <div className="flex items-center gap-3 mb-6">
              {currentSection.icon}
              <h2 className="text-2xl font-bold text-charcoal-900">{currentSection.title}</h2>
            </div>
            {currentSection.content}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-8 text-center">
        <h3 className="text-2xl font-bold text-charcoal-900 mb-3">
          Master the Visual Grammar
        </h3>
        <p className="text-charcoal-700 mb-6 max-w-2xl mx-auto">
          Use this systematic approach to create clear, consistent diagrams that communicate complex ideas with precision and clarity.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a href="/elevation/best-practices-design-principles" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium text-white bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700">
            <Star className="h-5 w-5 mr-2" />
            View Design Principles
          </a>
          <a href="/elevation/visual-inspiration-direction" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium text-white bg-sage-600 hover:bg-sage-700">
            <Eye className="h-5 w-5 mr-2" />
            See Inspiration
          </a>
          <a href="/elevation/lab/brand-guidelines-document" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium text-white bg-[var(--mustard)] hover:bg-[var(--mustard)]">
            <BookOpen className="h-5 w-5 mr-2" />
            Read Guidelines
          </a>
        </div>
      </div>
    </div>
  );
};
